import { Bookmark, BookmarkCheck } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import StarRating from "../../../components/starRating/StarRating";
import TransparentButton from "../../../components/transparentButton/TransparentButton";
import dummyImage from "../../../images/background/dummy-course-image.png";
import { api1 } from "../../../utils/axiosInstance";
import { useSelector } from "react-redux";
import CommonDropdown from "../../../components/commonDropDown/CommonDropDown";
import { useForm } from "react-hook-form";
import { courseProgress } from "../../../constants/constants";
import Modal from "../../../components/modal/Modal";

const CoursesCard = ({
  courseId,
  title,
  url,
  description,
  rating = 0,
  numProductRatings,
  imageUrl,
  skills,
  productDifficultyLevel,
  partners,
  isBookmarked,
  onBookmarkToggle,
  onClick,
  status,
  onStatusChange,
}) => {
  const userId = useSelector((state) => state?.auth?.id);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const isUserInitiatedChange = useRef(false);

  const {
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      progressStatus: status || "",
    },
  });

  // Watch dropdown value
  const progressStatus = watch("progressStatus");

  // Update form values when status prop changes (from parent)
  useEffect(() => {
    if (status && !isUserInitiatedChange.current) {
      reset({ progressStatus: status });
    }
  }, [status, reset]);

  // Handle status changes differently based on source
  useEffect(() => {
    // Only respond to user-initiated changes
    if (
      progressStatus &&
      progressStatus !== status &&
      isUserInitiatedChange.current
    ) {
      setSelectedStatus(progressStatus);
      setIsModalOpen(true);
      // Reset the flag after handling the change
      isUserInitiatedChange.current = false;
    }
  }, [progressStatus, status]);

  // Set up the register with a custom onChange
  const registerWithCustomOnChange = () => {
    const registerResult = register("progressStatus");

    return {
      ...registerResult,
      onChange: (e) => {
        // Set flag to indicate this is a user action
        isUserInitiatedChange.current = true;
        // Call the original onChange
        registerResult.onChange(e);
      },
    };
  };

  // Handle Bookmark Toggle
  const [isLoading, setIsLoading] = useState(false);

  const toggleBookmark = async (e) => {
    e.stopPropagation();
    setIsLoading(true);

    try {
      await api1.post("/api/courses/toggle-bookmark", {
        userId,
        courseId,
        title,
        url,
        description,
        imageUrl,
        rating,
        numProductRatings,
        skills,
        productDifficultyLevel,
        partners,
      });

      onBookmarkToggle(
        {
          courseId,
          title,
          url,
          description,
          imageUrl,
          rating,
          numProductRatings,
          skills,
          productDifficultyLevel,
          partners,
        },
        isBookmarked
      );
    } catch (error) {
      console.error("Error bookmarking course", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm Status Change
  const confirmStatusChange = async () => {
    try {
      await api1.post("/api/courses/update-status", {
        userId,
        courseId,
        status: selectedStatus,
      });
      onStatusChange(courseId, selectedStatus);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error updating bookmark status", error);
      // Reset to original status if API call fails
      setValue("progressStatus", status);
      setIsModalOpen(false);
    }
  };

  // Cancel Status Change
  const cancelStatusChange = () => {
    // Reset dropdown to previous value
    setValue("progressStatus", status);
    setIsModalOpen(false);
  };

  return (
    <button
      onClick={onClick}
      className="relative p-2 shadow-lg flex flex-col gap-3 h-[380px] rounded-lg 
             transition-transform duration-300 ease-in-out hover:shadow-xl hover:scale-[1.02] bg-white"
    >
      {/* Bookmark Icon */}
      {isBookmarked && (
        <div
          onClick={toggleBookmark}
          className="absolute top-3 right-3 bg-white p-1 rounded-full shadow-md cursor-pointer"
        >
          <BookmarkCheck className="text-custom-blue w-5 h-5" />
        </div>
      )}

      {/* Course Image */}
      <img
        src={imageUrl || dummyImage}
        alt={title}
        className="w-full h-40 object-cover rounded-md"
      />

      {/* Course Details */}
      <div className="flex flex-col flex-grow">
        <p className="text-custom-blue font-medium text-lg line-clamp-2">
          {title}
        </p>

        {description && (
          <p className="font-normal text-xs text-inactive-gray my-3 line-clamp-3">
            {description}
          </p>
        )}

        {/* Rating Section */}
        <div className="flex gap-1 items-center mt-auto">
          <p className="font-normal text-xs text-inactive-gray">
            {rating.toFixed(1)}
          </p>
          <StarRating rating={Math.round(rating)} readOnly />
          <p className="font-normal text-xs text-inactive-gray">
            ({numProductRatings || 0})
          </p>
        </div>
      </div>

      {/* View Details Button or Status Dropdown */}
      {isBookmarked ? (
        <div onClick={(e) => e.stopPropagation()}>
          <CommonDropdown
            name="progressStatus"
            register={registerWithCustomOnChange}
            defaultValue={status || ""}
            errors={errors}
            options={courseProgress}
          />
        </div>
      ) : (
        <div className="mt-auto">
          <TransparentButton
            onClick={toggleBookmark}
            buttonText={isLoading ? "Adding..." : "Add to Learning Plan"}
            disabled={isLoading} // ✅ Disable button when loading
            buttonClass={`border border-custom-blue p-4 w-full font-semibold text-base rounded-md 
    ${
      isLoading
        ? "bg-gray-300 text-gray-600 cursor-not-allowed" // ✅ Disabled styles
        : "hover:bg-custom-blue text-custom-blue hover:text-white"
    }`}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={cancelStatusChange}>
          <div className="p-4">
            <h2 className="text-lg font-semibold">Confirm Status Change</h2>
            <p className="text-sm text-gray-600 mt-2">
              Are you sure you want to change the status of this bookmarked
              course to <strong>{selectedStatus}</strong>?
            </p>

            <div className="flex justify-end mt-4">
              <button
                onClick={cancelStatusChange}
                className="px-4 py-2 mr-2 text-gray-600 border border-gray-300 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className="px-4 py-2 text-white bg-custom-blue rounded-md"
              >
                Yes, Change
              </button>
            </div>
          </div>
        </Modal>
      )}
    </button>
  );
};

export default CoursesCard;
